import { useEffect, useMemo, useRef, useState } from "react";

// === FullGraphViz ===========================================================
// A professional, self-contained SVG node-link diagram of the WHOLE visible
// memory graph (every visible note + every edge between visible notes), as
// opposed to EgoGraphViz which only renders the active note + its direct
// links.
//
// Interaction: Obsidian-style PAN + ZOOM. Scroll/trackpad-pinch zooms toward
// the cursor; drag the background to pan; +/−/Reset controls are overlaid.
// A click (no drag) on a node selects it.
//
// Layout is a DETERMINISTIC force-directed simulation computed synchronously
// in useMemo. Determinism is load-bearing: Math.random / Date.now are BANNED
// (they would make every render jump and break snapshot stability). Node
// positions are seeded on a circle by index, then relaxed over a fixed number
// of iterations of repulsion + spring + gravity. Identical input => identical
// output, every render. (Pan/zoom is view-only state and never changes the
// layout.)
// ============================================================================

export interface FullGraphNode {
  id: string;
  label: string;
  type?: string;
  isPrivate?: boolean;
}

export interface FullGraphEdge {
  from: string;
  to: string;
}

interface Props {
  nodes: FullGraphNode[];
  edges: FullGraphEdge[];
  activeId: string | null;
  onSelect: (id: string) => void;
}

// viewBox dimensions — the coordinate space the simulation lays out in.
const W = 640;
const H = 420;
const PADDING = 36;

// Zoom bounds for the pan/zoom view transform.
const MIN_K = 0.35;
const MAX_K = 5;

// Simulation constants. Tuned for a small (<~60-node) memory graph; all
// deterministic so the diagram is stable across renders. Spread is generous
// (large rest length + repulsion) so nodes don't pile up — pan/zoom then lets
// the user inspect any cluster.
const ITERATIONS = 220;
const REPULSION = 22000; // strength of pairwise inverse-square repulsion
const REPULSION_CAP = 1.2; // cap per-pair displacement so dense clusters don't explode
const SPRING = 0.045; // Hooke constant for edge attraction
const REST_LENGTH = 130; // ideal edge length
const GRAVITY = 0.016; // mild pull toward the center
const DAMPING = 0.9; // velocity damping per step

// Node fill tone by entity type. CSS vars with hard-coded fallbacks so the
// diagram looks right even outside the Security Room theme context.
const TONE: Record<string, string> = {
  "security-note": "var(--accent,#3b82f6)",
  "policy-decision": "var(--amber,#e0a64a)",
  "dlp-hit": "var(--purple,#b79cff)",
  "approval-resolved": "var(--green,#4ec27a)",
  "pr-risk-brief": "var(--red,#f06b62)",
};
const TONE_DEFAULT = "var(--muted,#8a8e96)";

function toneFor(type?: string): string {
  return (type && TONE[type]) || TONE_DEFAULT;
}

function truncate(label: string): string {
  return label.length > 18 ? `${label.slice(0, 17)}…` : label;
}

interface Placed {
  id: string;
  label: string;
  type?: string;
  isPrivate?: boolean;
  x: number;
  y: number;
}

interface PlacedEdge {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  key: string;
}

interface Layout {
  placed: Placed[];
  edges: PlacedEdge[];
}

function computeLayout(nodes: FullGraphNode[], edges: FullGraphEdge[]): Layout {
  const n = nodes.length;
  if (n === 0) return { placed: [], edges: [] };

  const cx = W / 2;
  const cy = H / 2;

  // Seed positions deterministically on a circle by index. A small radius
  // ensures the simulation does the spreading rather than the seed.
  const seedR = Math.min(W, H) / 3;
  const xs = new Array<number>(n);
  const ys = new Array<number>(n);
  const vx = new Array<number>(n).fill(0);
  const vy = new Array<number>(n).fill(0);
  for (let i = 0; i < n; i++) {
    const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
    xs[i] = cx + seedR * Math.cos(angle);
    ys[i] = cy + seedR * Math.sin(angle);
  }

  // Index map for edge endpoints (skip edges whose endpoints aren't present).
  const indexOf = new Map<string, number>();
  for (let i = 0; i < n; i++) indexOf.set(nodes[i].id, i);
  const edgePairs: Array<[number, number]> = [];
  for (const e of edges) {
    const a = indexOf.get(e.from);
    const b = indexOf.get(e.to);
    if (a === undefined || b === undefined || a === b) continue;
    edgePairs.push([a, b]);
  }

  for (let iter = 0; iter < ITERATIONS; iter++) {
    const fx = new Array<number>(n).fill(0);
    const fy = new Array<number>(n).fill(0);

    // Pairwise repulsion — inverse-square, capped per pair.
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        let dx = xs[i] - xs[j];
        let dy = ys[i] - ys[j];
        let distSq = dx * dx + dy * dy;
        if (distSq < 0.01) {
          // Deterministic nudge to break perfect overlap (index-based, no RNG).
          dx = (i - j) * 0.1 + 0.1;
          dy = (j - i) * 0.1 + 0.1;
          distSq = dx * dx + dy * dy;
        }
        const dist = Math.sqrt(distSq);
        let force = REPULSION / distSq;
        if (force > REPULSION_CAP * 1000) force = REPULSION_CAP * 1000;
        const ux = dx / dist;
        const uy = dy / dist;
        fx[i] += ux * force * 0.001;
        fy[i] += uy * force * 0.001;
        fx[j] -= ux * force * 0.001;
        fy[j] -= uy * force * 0.001;
      }
    }

    // Spring attraction along edges toward the rest length.
    for (const [a, b] of edgePairs) {
      const dx = xs[b] - xs[a];
      const dy = ys[b] - ys[a];
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const disp = (dist - REST_LENGTH) * SPRING;
      const ux = dx / dist;
      const uy = dy / dist;
      fx[a] += ux * disp;
      fy[a] += uy * disp;
      fx[b] -= ux * disp;
      fy[b] -= uy * disp;
    }

    // Mild gravity toward center + integrate with damping.
    for (let i = 0; i < n; i++) {
      fx[i] += (cx - xs[i]) * GRAVITY;
      fy[i] += (cy - ys[i]) * GRAVITY;
      vx[i] = (vx[i] + fx[i]) * DAMPING;
      vy[i] = (vy[i] + fy[i]) * DAMPING;
      xs[i] += vx[i];
      ys[i] += vy[i];
    }
  }

  // Clamp final coords within the viewBox with padding.
  const placed: Placed[] = nodes.map((node, i) => ({
    id: node.id,
    label: node.label,
    type: node.type,
    isPrivate: node.isPrivate,
    x: Math.max(PADDING, Math.min(W - PADDING, xs[i])),
    y: Math.max(PADDING, Math.min(H - PADDING, ys[i])),
  }));

  const posById = new Map<string, Placed>();
  for (const p of placed) posById.set(p.id, p);

  const placedEdges: PlacedEdge[] = [];
  for (let k = 0; k < edges.length; k++) {
    const e = edges[k];
    const a = posById.get(e.from);
    const b = posById.get(e.to);
    if (!a || !b || a.id === b.id) continue;
    placedEdges.push({
      x1: a.x,
      y1: a.y,
      x2: b.x,
      y2: b.y,
      key: `${e.from}->${e.to}-${k}`,
    });
  }

  return { placed, edges: placedEdges };
}

interface View {
  k: number;
  tx: number;
  ty: number;
}

export function FullGraphViz({ nodes, edges, activeId, onSelect }: Props) {
  const layout = useMemo(() => computeLayout(nodes, edges), [nodes, edges]);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [view, setView] = useState<View>({ k: 1, tx: 0, ty: 0 });
  // Drag state in a ref so pointer handlers don't re-bind every render. `moved`
  // distinguishes a pan-drag from a click so node selection isn't triggered at
  // the end of a pan.
  const drag = useRef({ active: false, x: 0, y: 0, tx: 0, ty: 0, moved: false });

  // Convert client pixel coords → the SVG's viewBox coordinate space (the svg
  // is width:100% with a fixed viewBox, so scale by the rendered/ viewBox ratio).
  function toViewBox(clientX: number, clientY: number): { x: number; y: number } {
    const el = svgRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return { x: ((clientX - r.left) / r.width) * W, y: ((clientY - r.top) / r.height) * H };
  }

  // Wheel/trackpad zoom anchored at the cursor. Registered as a NON-passive
  // listener so preventDefault() actually stops the page/pane from scrolling.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const { x: vbX, y: vbY } = toViewBox(e.clientX, e.clientY);
      setView((v) => {
        const factor = Math.exp(-e.deltaY * 0.0015);
        const k = Math.min(MAX_K, Math.max(MIN_K, v.k * factor));
        // Keep the graph point under the cursor fixed across the zoom.
        const gx = (vbX - v.tx) / v.k;
        const gy = (vbY - v.ty) / v.k;
        return { k, tx: vbX - gx * k, ty: vbY - gy * k };
      });
    }
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  function onPointerDown(e: React.PointerEvent) {
    drag.current = {
      active: true,
      x: e.clientX,
      y: e.clientY,
      tx: view.tx,
      ty: view.ty,
      moved: false,
    };
    svgRef.current?.setPointerCapture?.(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!drag.current.active) return;
    const el = svgRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const dxClient = e.clientX - drag.current.x;
    const dyClient = e.clientY - drag.current.y;
    if (Math.abs(dxClient) + Math.abs(dyClient) > 3) drag.current.moved = true;
    const dx = (dxClient / r.width) * W;
    const dy = (dyClient / r.height) * H;
    setView((v) => ({ ...v, tx: drag.current.tx + dx, ty: drag.current.ty + dy }));
  }
  function endDrag(e: React.PointerEvent) {
    drag.current.active = false;
    svgRef.current?.releasePointerCapture?.(e.pointerId);
  }

  function zoomAroundCenter(factor: number) {
    setView((v) => {
      const k = Math.min(MAX_K, Math.max(MIN_K, v.k * factor));
      const gx = (W / 2 - v.tx) / v.k;
      const gy = (H / 2 - v.ty) / v.k;
      return { k, tx: W / 2 - gx * k, ty: H / 2 - gy * k };
    });
  }
  function reset() {
    setView({ k: 1, tx: 0, ty: 0 });
  }

  function handleNodeClick(id: string) {
    // Suppress selection if this pointer interaction was a pan-drag.
    if (!drag.current.moved) onSelect(id);
  }

  const empty = nodes.length === 0;

  return (
    <div className="sr-fullgraph">
      {!empty && (
        <div className="sr-fullgraph__controls">
          <button
            type="button"
            className="sr-fullgraph__btn"
            aria-label="Zoom in"
            onClick={() => zoomAroundCenter(1.3)}
          >
            +
          </button>
          <button
            type="button"
            className="sr-fullgraph__btn"
            aria-label="Zoom out"
            onClick={() => zoomAroundCenter(1 / 1.3)}
          >
            −
          </button>
          <button
            type="button"
            className="sr-fullgraph__btn sr-fullgraph__btn--reset"
            aria-label="Reset view"
            onClick={reset}
          >
            Reset
          </button>
        </div>
      )}
      <svg
        ref={svgRef}
        className="sr-fullgraph__svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Memory relationship graph — scroll to zoom, drag to pan"
        style={{ cursor: drag.current.active ? "grabbing" : "grab", touchAction: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        {empty ? (
          <text
            x={W / 2}
            y={H / 2}
            textAnchor="middle"
            fontSize={13}
            fontFamily="var(--font-sans)"
            fill="var(--muted)"
          >
            No notes to graph
          </text>
        ) : (
          <g transform={`translate(${view.tx} ${view.ty}) scale(${view.k})`}>
            {/* Edges first so nodes paint on top. */}
            {layout.edges.map((e) => (
              <line
                key={e.key}
                x1={e.x1}
                y1={e.y1}
                x2={e.x2}
                y2={e.y2}
                stroke="var(--line-strong,#34353c)"
                strokeOpacity={0.65}
                strokeWidth={1}
              />
            ))}
            {layout.placed.map((node) => {
              const isActive = node.id === activeId;
              const tone = toneFor(node.type);
              return (
                <g
                  key={node.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleNodeClick(node.id)}
                >
                  <title>{node.label}</title>
                  {isActive && (
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={17}
                      fill="none"
                      stroke={tone}
                      strokeOpacity={0.35}
                      strokeWidth={2}
                    />
                  )}
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={isActive ? 11 : 7}
                    fill={tone}
                    stroke="var(--panel,#161618)"
                    strokeWidth={2}
                  />
                  <text
                    x={node.x}
                    y={node.y + (isActive ? 28 : 22)}
                    textAnchor="middle"
                    fontSize={10}
                    fontFamily="var(--font-sans)"
                    fill={isActive ? "var(--text,#e6e7ea)" : "var(--muted)"}
                    fontWeight={isActive ? 600 : 400}
                  >
                    {truncate(node.label)}
                    {node.isPrivate && (
                      <tspan fill="var(--muted)" fillOpacity={0.6}>
                        {" ·private"}
                      </tspan>
                    )}
                  </text>
                </g>
              );
            })}
          </g>
        )}
      </svg>
    </div>
  );
}
